import {HttpClient} from '@angular/common/http'
import {inject, Service, signal} from '@angular/core'
import {BlancoLoanApplication} from '@sparbanken-syd/blanco-backend'
import {ConfigService, SparbankenUser} from '@sparbanken-syd/sparbanken-syd-bankid'
import {BehaviorSubject} from 'rxjs'
import {filter} from 'rxjs/operators'
import {environment} from '../../environments/environment'
import {IStats} from './types'
import {MatDialog} from '@angular/material/dialog'
import {WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'

@Service()
export class AdminService {
  /**
   * All users of sorts.
   */
  public users$ = new BehaviorSubject<SparbankenUser[]>([])

  /**
   * A subject that we can subscribe to
   */
  public applications$ = new BehaviorSubject<BlancoLoanApplication[]>([{blancoStatus: -1} as any])

  public statistics = signal<IStats>({} as IStats)

  /**
   * The current application we are working with...
   */
  public currentApplication$ = new BehaviorSubject<BlancoLoanApplication>({codes: []} as any)

  /**
   * Depending on application state we have a latest fetch time that differs.
   */
  private fetchTimeStamps = {
    created: 0,
    closed: 0,
    active: 0
  }

  private readonly configService = inject(ConfigService)
  private readonly httpClient = inject(HttpClient)
  private readonly dialog = inject(MatDialog)

  constructor() {
    this.configService.isAdmin$
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.getUsers()
        this.getApplications('active')
        this.getStatistics()
      })
  }

  public getApplication(id: string | null): void {
    const url = `${environment.loginServiceUrl}/admin/blanco/${id}`
    this.httpClient.get<BlancoLoanApplication>(url).subscribe(
      (application: BlancoLoanApplication) => {
        this.currentApplication$.next(application)
      })
  }

  /**
   * Saves an application, only adminData, codes and status may change
   * this is enforced by the backend.
   */
  public saveApplication(application: BlancoLoanApplication | undefined): void {
    const url = `${environment.loginServiceUrl}/admin/blanco/${application?.itemId}`
    this.httpClient.put<BlancoLoanApplication>(url, application)
      .subscribe((app: BlancoLoanApplication) => {
        const apps = this.applications$.value.map(ca => {
          if (ca.itemId === app.itemId) {
            return app
          }
          return ca
        })
        this.applications$.next(apps)
        this.currentApplication$.next(app)
      })
  }


  public getApplications(state: string): void {
    const dialogRef = this.dialog.open(WaitComponent, {
      disableClose: true,
      data: {
        title: 'Updating',
        progress: true
      }
    })

    const url = `${environment.loginServiceUrl}/admin/blanco?state=${state}&timeStamp=${this.fetchTimeStamps[state]}`
    this.httpClient.get<BlancoLoanApplication[]>(url)
      .subscribe((applications: BlancoLoanApplication[]) => {
        this.fetchTimeStamps[state] = Date.now()
        applications = applications.concat(this.applications$.getValue())
        applications = applications
          .filter(a => a.blancoStatus !== -1)
        this.applications$.next(applications)
        dialogRef.close()
      })
  }

  public getStatistics(): void {
    const url = `${environment.loginServiceUrl}/admin/statistics`
    this.httpClient.get<IStats>(url)
      .subscribe(results => this.statistics.set(results))
  }

  public getUcReport(id: string | undefined) {
    const url = `${environment.loginServiceUrl}/uc`
    return this.httpClient.put<{ report: string }>(url, {personNummer: id})
  }

  /**
   * Fetch a list of users, send them on the sub if not already
   * fetched. Only blancoAdmins are presented.
   */
  private getUsers(): void {
    const url = `${environment.authServiceUrl}/users` // ?${new Date().getTime()}
    this.httpClient.get<SparbankenUser[]>(url)
      .subscribe((users: SparbankenUser[]) => {
        this.users$.next(users.filter(user => user.roles.includes('blancoAdmin')))
      })
  }
}
